import React from 'react';
import './SessionDetailHeader.scss';
import {
  formatDuration,
  formatSessionDateTime,
  SessionExplorerSession,
} from '../shared/sessionExplorerList';

interface SessionDetailHeaderProps {
  session: SessionExplorerSession;
  // Rendered on the right of the title row (e.g. the filter when it sticks).
  actions?: React.ReactNode;
}

const SessionDetailHeader: React.FC<SessionDetailHeaderProps> = ({ session, actions }) => {
  const primaryResponder = session.agents[0];
  const primaryResponderName = primaryResponder?.name ?? 'Unknown responder';
  const primaryResponderIcon =
    primaryResponder?.emitter_role === 'Agent'
      ? 'support_agent'
      : primaryResponder?.emitter_role === 'Bot'
      ? 'smart_toy'
      : 'person_outline';
  const customerName = session.customer?.name ?? 'Unknown customer';
  const contactReason = session.contact_reason ?? 'No contact reason recorded';
  const formattedDuration = formatDuration(session.duration_seconds);
  const readableCategories = session.categories.filter(
    (category) => !/^Support > [0-9a-f-]{36}$/i.test(category.path)
  );

  return (
    <header className="ca-session-header" aria-labelledby="ca-session-header-title">
      <div className="ca-session-header__main">
        <div className="ca-session-header__eyebrow">
          <span className="ca-session-header__eyebrow-label">Session</span>
          <span className="ca-session-header__eyebrow-separator" aria-hidden="true">
            ·
          </span>
          <span className="ca-session-header__id" title={session.session_id}>
            {session.session_id}
          </span>
          <span className="ca-session-header__eyebrow-separator" aria-hidden="true">
            ·
          </span>
          <time className="ca-session-header__date" dateTime={session.start_datetime ?? undefined}>
            {formatSessionDateTime(session.start_datetime, 'UTC')}
          </time>
        </div>
        <h2 className="ca-session-header__title" id="ca-session-header-title" title={contactReason}>
          {contactReason}
        </h2>
        <div className="ca-session-header__metadata" aria-label="Session details">
          <span className="ca-session-header__metadata-item" title={primaryResponderName}>
            <span className="material-icons" aria-hidden="true">
              {primaryResponderIcon}
            </span>
            {primaryResponderName}
          </span>
          <span className="ca-session-header__metadata-item" title={customerName}>
            <span className="material-icons" aria-hidden="true">
              person
            </span>
            {customerName}
          </span>
          <span className="ca-session-header__metadata-item">
            <span className="material-icons" aria-hidden="true">
              chat_bubble_outline
            </span>
            {session.message_count != null ? `${session.message_count} messages` : 'Unknown'}
          </span>
          <span className="ca-session-header__metadata-item">
            <span className="material-icons" aria-hidden="true">
              schedule
            </span>
            {formattedDuration}
          </span>
        </div>
        {readableCategories.length > 0 && (
          <div className="ca-session-header__categories" aria-label="Categories" role="list">
            {readableCategories.map((category) => (
              <span
                key={`${category.id}-${category.path}`}
                className="ca-session-header__category"
                role="listitem"
                title={category.path}
              >
                <span className="material-icons" aria-hidden="true">
                  account_tree
                </span>
                {category.path}
              </span>
            ))}
          </div>
        )}
      </div>

      {actions && <div className="ca-session-header__actions">{actions}</div>}
    </header>
  );
};

export default SessionDetailHeader;
